import { useEffect, useState } from "react";
import { Flame } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Iter133 — Streak Leaders widget for the manager dashboard.
 *
 * Pulls `/api/leaderboard/team-streaks?limit=5` once on mount and renders
 * a compact card listing the team's top 5 active close-streaks. Hidden
 * entirely when no rep has a 2+ day streak (avoids a sad-empty-state on
 * accounts that haven't seeded enough deal history).
 *
 * Visual style mirrors the rep-side StreakBadge: amber for 2–3 days,
 * orange for 7+, rose for 14+. Top streak gets a flame icon.
 */
function streakTone(days) {
  if (days >= 14) return "text-rose-300";
  if (days >= 7) return "text-orange-300";
  if (days >= 4) return "text-amber-200";
  return "text-amber-300";
}

function initials(name) {
  return (name || "?")
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function StreakLeadersWidget() {
  const [leaders, setLeaders] = useState(null); // null = loading, [] = no leaders
  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const res = await fetch(`${API}/leaderboard/team-streaks?limit=5`, { credentials: "include" });
        if (!res.ok) {
          if (!aborted) setLeaders([]);
          return;
        }
        const data = await res.json();
        if (!aborted) setLeaders(data?.leaders || []);
      } catch (e) {
        if (!aborted) setLeaders([]);
      }
    })();
    return () => { aborted = true; };
  }, []);

  // Loading skeleton.
  if (leaders === null) {
    return (
      <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-5">
        <div className="h-4 w-32 bg-white/10 rounded mb-4 animate-pulse" />
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3 animate-pulse">
              <div className="w-8 h-8 rounded-full bg-white/10" />
              <div className="flex-1 h-3 bg-white/10 rounded" />
              <div className="w-12 h-3 bg-white/10 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // No 2+ day streaks → hide the widget entirely (avoids a "no streaks
  // yet" downer card on the dashboard).
  if (leaders.length === 0) return null;

  return (
    <div
      className="bg-[#1A1A1A] border border-amber-500/20 rounded-xl p-5"
      data-testid="streak-leaders-widget"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4 text-amber-400" />
          <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
            Streak Leaders
          </h3>
        </div>
        <span className="text-[10px] text-gray-500">consecutive close-days</span>
      </div>
      <ul className="space-y-2.5">
        {leaders.map((rep, idx) => (
          <li
            key={rep.user_id}
            className="flex items-center gap-3"
            data-testid={`streak-leader-row-${idx}`}
            title={
              rep.includes_today
                ? `On a ${rep.streak}-day close streak — last close ${rep.last_close_date}`
                : `${rep.streak}-day streak from yesterday — at risk if no close today`
            }
          >
            <span
              className={`text-xs font-bold w-5 ${
                idx === 0 ? "text-yellow-400" : "text-gray-500"
              }`}
            >
              #{idx + 1}
            </span>
            <Avatar className="w-8 h-8">
              {rep.picture && <AvatarImage src={rep.picture} alt={rep.name} />}
              <AvatarFallback className="bg-emerald-500/20 text-emerald-300 text-xs font-semibold">
                {initials(rep.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate">{rep.name}</div>
              {rep.role && (
                <div className="text-[10px] text-gray-500 capitalize truncate">{rep.role}</div>
              )}
            </div>
            <div className={`flex items-center gap-1 ${streakTone(rep.streak)}`}>
              <Flame className="w-3.5 h-3.5" />
              <span className="text-sm font-bold tabular-nums">{rep.streak}</span>
              <span className="text-[10px] text-gray-500 hidden sm:inline">days</span>
              {/* Iter183 — badge showing this streak includes a
                  preserved segment from a recent break + recovery. */}
              {rep.preserved_from_break && rep.bridged_from_streak && (
                <span
                  className="ml-1 inline-flex items-center gap-0.5 text-[9px] font-semibold uppercase tracking-wider text-orange-300 bg-orange-500/10 border border-orange-500/30 px-1.5 py-0.5 rounded-full"
                  title={`${rep.bridged_from_streak} days preserved from a pre-break streak — recovered within 14 days`}
                  data-testid={`streak-preserved-${rep.user_id}`}
                >
                  🔥 {rep.bridged_from_streak} preserved
                </span>
              )}
              {!rep.includes_today && (
                <span
                  className="ml-1 text-[9px] uppercase tracking-wider text-amber-200/60 hidden md:inline"
                  title="Built from yesterday — at risk if no close today"
                >
                  at risk
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
